import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { globalSearch } from "api/Search";
import SearchIcon from "components/svg/icons/SearchIcon";
import {
  Backdrop,
  SearchModal,
  SearchInputWrapper,
  SearchInput,
  SearchShortcut,
  ResultsContainer,
  ResultsSection,
  SectionHeader,
  ResultItem,
  ResultLabel,
  ResultSublabel,
  EmptyState,
  LoadingWrapper,
  HighlightMark,
  Footer,
  FooterKeys,
  FooterKey,
} from "./styled";

interface SearchResultItem {
  id: string;
  label: string;
  sublabel: string;
  type: string;
  sequence?: number;
}

interface FlatResult extends SearchResultItem {
  section: string;
  route: string;
}

const SECTION_CONFIG: Record<string, { label: string; routePrefix: string }> = {
  projects: { label: "Projects", routePrefix: "/projects" },
  purchaseOrders: { label: "Purchase Orders", routePrefix: "/purchase-orders" },
  invoices: { label: "Invoices", routePrefix: "/invoices" },
  quotes: { label: "Quotes", routePrefix: "/quotes" },
  quoteRequests: { label: "Quote Requests", routePrefix: "/quote-requests" },
  clients: { label: "Clients", routePrefix: "/clients" },
  leads: { label: "Leads", routePrefix: "/leads" },
  providers: { label: "Providers", routePrefix: "/providers" },
  // Session 69 - maintenance records. Routes confirmed against the app's
  // own navigation: WorkOrderDetail is reached via
  // `/maintenance/work-orders/:id` and PlanDetail via
  // `/maintenance/plans/:id`.
  workOrders: { label: "Work Orders", routePrefix: "/maintenance/work-orders" },
  maintenancePlans: { label: "Maintenance Plans", routePrefix: "/maintenance/plans" },
};

const SECTION_ORDER = [
  "projects",
  "purchaseOrders",
  "invoices",
  "quotes",
  "quoteRequests",
  "workOrders",
  "maintenancePlans",
  "clients",
  "leads",
  "providers",
];

const highlightText = (text: string, query: string) => {
  if (!query || query.length < 2) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <HighlightMark key={i}>{part}</HighlightMark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearch = ({ isOpen, onClose }: GlobalSearchProps) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Record<string, SearchResultItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounceRef = useRef<any>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Flatten results for keyboard nav
  const flatResults: FlatResult[] = useMemo(() => {
    const flat: FlatResult[] = [];
    for (const key of SECTION_ORDER) {
      const items = results[key];
      if (!items || items.length === 0) continue;
      const config = SECTION_CONFIG[key];
      if (!config) continue;
      for (const item of items) {
        flat.push({
          ...item,
          section: key,
          route: `${config.routePrefix}/${item.id}`,
        });
      }
    }
    return flat;
  }, [results]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery("");
      setResults({});
      setActiveIndex(0);
    }
  }, [isOpen]);

  // Debounced search
  const doSearch = useCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setResults({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await globalSearch(term, 5);
      setResults(data.results || {});
      setActiveIndex(0);
    } catch (e: any) {
      setResults({});
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleNavigate = (route: string) => {
    navigate(route);
    onClose();
  };

  // Keyboard nav
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatResults[activeIndex];
      if (item) handleNavigate(item.route);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!resultsContainerRef.current) return;
    const activeEl = resultsContainerRef.current.querySelector("[data-active='true']");
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // Backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  const hasResults = flatResults.length > 0;
  const hasQuery = query.trim().length >= 2;

  // Build sections for rendering
  let runningIndex = 0;
  const sections: { key: string; label: string; items: (FlatResult & { flatIndex: number })[] }[] = [];
  for (const key of SECTION_ORDER) {
    const items = results[key];
    if (!items || items.length === 0) continue;
    const config = SECTION_CONFIG[key];
    if (!config) continue;
    const sectionItems: (FlatResult & { flatIndex: number })[] = [];
    for (const item of items) {
      sectionItems.push({
        ...item,
        section: key,
        route: `${config.routePrefix}/${item.id}`,
        flatIndex: runningIndex,
      });
      runningIndex++;
    }
    sections.push({ key, label: config.label, items: sectionItems });
  }

  return (
    <Backdrop onClick={handleBackdropClick}>
      <SearchModal onKeyDown={handleKeyDown}>
        <SearchInputWrapper>
          <SearchIcon width="18px" height="18px" fill="#818181" />
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="Search work orders, projects, invoices, clients..."
            value={query}
            onChange={handleInputChange}
            autoComplete="off"
            spellCheck={false}
          />
          <SearchShortcut>ESC</SearchShortcut>
        </SearchInputWrapper>

        <ResultsContainer ref={resultsContainerRef}>
          {loading && hasQuery && (
            <LoadingWrapper>Searching...</LoadingWrapper>
          )}

          {!loading && hasQuery && !hasResults && (
            <EmptyState>No results found for &ldquo;{query}&rdquo;</EmptyState>
          )}

          {!loading &&
            sections.map((section) => (
              <ResultsSection key={section.key}>
                <SectionHeader>{section.label}</SectionHeader>
                {section.items.map((item) => (
                  <ResultItem
                    key={item.id}
                    $active={item.flatIndex === activeIndex}
                    data-active={item.flatIndex === activeIndex ? "true" : "false"}
                    onClick={() => handleNavigate(item.route)}
                    onMouseEnter={() => setActiveIndex(item.flatIndex)}
                  >
                    <ResultLabel>{highlightText(item.label, query)}</ResultLabel>
                    <ResultSublabel>{highlightText(item.sublabel, query)}</ResultSublabel>
                  </ResultItem>
                ))}
              </ResultsSection>
            ))}
        </ResultsContainer>

        {hasResults && (
          <Footer>
            <FooterKeys>
              <FooterKey>{"\u2191\u2193"}</FooterKey> navigate
              <FooterKey>{"\u21B5"}</FooterKey> open
              <FooterKey>esc</FooterKey> close
            </FooterKeys>
            <span>{flatResults.length} result{flatResults.length !== 1 ? "s" : ""}</span>
          </Footer>
        )}
      </SearchModal>
    </Backdrop>
  );
};

export default GlobalSearch;
